import {
  BigDotIcon,
  Edit1Icon,
  Grid2,
  HistoryIcon,
  IconButton,
  Plus1Icon,
  Tooltip,
  Typography,
} from "components";
import GKButton from "components/GKButton";
import { Show } from "components/Show";
import SimpleLoader from "components/SimpleLoader";
import { useState } from "react";
import { useGetRevenueByPortfolioQuery } from "store/features/Revenue/FeePlan";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch } from "store/store";
import { matchDate } from "utils/helpers";
import { handlePercentage, numberToCurrency } from "utils/MathFunction";
import BillingDateModal from "./BillingDateModal";
import RevenueActionModal from "./RevenueActionModal";

interface StateInterface {
  open: boolean;
  type: string;
  modalTitle: string;
  width?: string;
  subTitle?: string;
}

const RevenueDetailsTab = (props: { portfolioId: string }) => {
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openActionModal, setOpenActionModal] = useState<StateInterface>({
    open: false,
    type: "",
    modalTitle: "",
    width: "40%",
    subTitle: "",
  });

  const { data, isFetching } = useGetRevenueByPortfolioQuery(
    props?.portfolioId,
  );

  if (isFetching) return <SimpleLoader />;

  const feeManagementData = [
    {
      name: "High Watermark",
      value: {
        amount: data?.data?.highWatermark?.highWatermark || 0,
        date: data?.data?.highWatermark?.date || "-",
      },
      button: {
        name: "Edit",
        icon: <Edit1Icon />,
        onclick: () =>
          setOpenActionModal({
            open: true,
            type: "HIGH_WATERMARK",
            modalTitle: "High Watermark History",
            width: "812px",
          }),
      },
    },
    {
      name: "Advance Fees",
      value: {
        amount: data?.data?.advanceFees?.advanceFees || 0,
        date: data?.data?.advanceFees?.updatedAt || "-",
      },
      button: {
        name: "History",
        icon: <HistoryIcon />,
        onclick: () =>
          setOpenActionModal({
            open: true,
            type: "ADVANCE_FEE_HISTORY",
            modalTitle: "Advance Fee History",
            width: "600px",
          }),
      },
    },
    {
      name: "Fee Deposit",
      value: {
        amount: data?.data?.advanceDeposit?.advanceDeposit || 0,
        date: data?.data?.advanceDeposit?.updatedAt || "-",
      },
      button: {
        name: "History",
        icon: <HistoryIcon />,
        onclick: () =>
          setOpenActionModal({
            open: true,
            type: "FEE_DEPOSIT_HISTORY",
            modalTitle: "Fee Deposit History",
            width: "600px",
          }),
      },
    },
  ];

  const billingDetails = [
    {
      name: "Total Billed Amount",
      value: { amount: data?.data?.totalBilled },
    },
    {
      name: "Total Fee Collected",
      value: { amount: data?.data?.totalCollected },
    },
  ];

  const handleRedirectProfileTab = () => {
    dispatch(TAB({ tabKey: "portfolioAdminViewTabs", tabValue: 0 }));
    dispatch(TAB({ tabKey: "portfolioProfileTab", tabValue: 0 }));
  };

  return (
    <Grid2 container spacing={4}>
      <Grid2 container size={12} spacing={2}>
        <Grid2 size={12}>
          <Typography
            fontSize={12}
            fontWeight={700}
            color={"var(--neutral-900)"}
          >
            Fee plans:
          </Typography>
        </Grid2>
        <Grid2
          size={8}
          p={"20px"}
          border={"1px solid var(--neutral-100)"}
          borderRadius={2}
          bgcolor={"var(--white)"}
        >
          <Grid2 container size={12} spacing={1}>
            <Grid2 container size={7} spacing={1}>
              <Grid2 size={12}>
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--neutral-700)"
                >
                  Current Fee Plan
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  color="var(--grey-900)"
                >
                  {data?.data?.feePlan?.feePlanName || "-"}
                </Typography>
              </Grid2>
            </Grid2>
            <Grid2 container size={5} spacing={1}>
              <Grid2
                size={12}
                display={"flex"}
                alignItems={"center"}
                gap={1}
                justifyContent={"flex-end"}
              >
                <GKButton
                  variant="outlined"
                  color="dark"
                  startIcon={<HistoryIcon />}
                  onClick={() =>
                    setOpenActionModal({
                      open: true,
                      type: "FEE_PLAN_HISTORY",
                      modalTitle: "Fee Plan History",
                      width: "840px",
                    })
                  }
                >
                  Plan History
                </GKButton>
                <GKButton
                  variant="contained"
                  startIcon={<Edit1Icon />}
                  onClick={() =>
                    setOpenActionModal({
                      open: true,
                      type: "FEE_PLAN_CHANGE",
                      modalTitle: "Change Fee Plan",
                      width: "840px",
                    })
                  }
                >
                  Change Plan
                </GKButton>
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
        <Grid2
          size={4}
          p={"20px"}
          border={"1px solid var(--neutral-100)"}
          borderRadius={2}
          bgcolor={"var(--white)"}
        >
          <Grid2 container size={12} spacing={1}>
            <Grid2 container size={7} spacing={1}>
              <Grid2 size={12}>
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--neutral-700)"
                >
                  Billing Date
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  color="var(--grey-900)"
                >
                  {matchDate(data?.data?.feePlan?.startingDate, "DD/MM/YYYY")}
                </Typography>
              </Grid2>
            </Grid2>
            <Grid2 container size={5} spacing={1}>
              <Grid2
                size={12}
                display={"flex"}
                alignItems={"center"}
                gap={1}
                justifyContent={"flex-end"}
              >
                <Tooltip title="Edit" arrow placement="top">
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      setAnchorEl(event.currentTarget);
                    }}
                  >
                    <Edit1Icon />
                  </IconButton>
                </Tooltip>
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>

      <Grid2 container size={12} spacing={2}>
        <Grid2 size={12}>
          <Typography
            fontSize={12}
            fontWeight={700}
            color={"var(--neutral-900)"}
          >
            Fee Management:
          </Typography>
        </Grid2>
        <Grid2 container size={12} spacing={3}>
          {feeManagementData.map((dataItem) => {
            return (
              <Grid2
                size={4}
                p={"20px"}
                border={"1px solid var(--neutral-100)"}
                borderRadius={2}
                bgcolor={"var(--white)"}
                key={dataItem?.name}
              >
                <Grid2 container size={12} spacing={1}>
                  <Grid2 container size={7} spacing={1}>
                    <Grid2 size={12}>
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        color="var(--neutral-700)"
                      >
                        {dataItem?.name}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        fontSize={14}
                        fontWeight={600}
                        color="var(--grey-900)"
                        display={"flex"}
                        alignItems={"center"}
                        gap={1}
                      >
                        {numberToCurrency(dataItem?.value?.amount || 0)}{" "}
                        <BigDotIcon />
                        <Typography component={"span"}>
                          {matchDate(dataItem.value.date, "DD/MM/YYYY")}
                        </Typography>
                      </Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container size={5} spacing={1}>
                    <Grid2
                      size={12}
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                      justifyContent={"flex-end"}
                    >
                      <Tooltip
                        title={dataItem?.button?.name}
                        arrow
                        placement="top"
                      >
                        <IconButton onClick={dataItem.button.onclick}>
                          {dataItem?.button?.icon}
                        </IconButton>
                      </Tooltip>
                    </Grid2>
                  </Grid2>
                </Grid2>
              </Grid2>
            );
          })}
        </Grid2>
      </Grid2>

      <Grid2 container size={12} spacing={2}>
        <Grid2 size={12}>
          <Typography
            fontSize={12}
            fontWeight={700}
            color={"var(--neutral-900)"}
          >
            Billing Details:
          </Typography>
        </Grid2>
        <Grid2 container size={12} spacing={3}>
          {billingDetails.map((dataItem) => {
            return (
              <Grid2
                size={4}
                p={"20px"}
                border={"1px solid var(--neutral-100)"}
                borderRadius={2}
                bgcolor={"var(--white)"}
                key={dataItem?.name}
              >
                <Grid2 size={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--neutral-700)"
                  >
                    {dataItem?.name}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography
                    fontSize={14}
                    fontWeight={600}
                    color="var(--grey-500)"
                  >
                    {numberToCurrency(dataItem?.value?.amount || 0)}{" "}
                  </Typography>
                </Grid2>
              </Grid2>
            );
          })}
        </Grid2>
      </Grid2>

      <Show.When isTrue={!!data?.data?.partnerSharing?.partner}>
        <Grid2 container size={12} spacing={2}>
          <Grid2 size={12}>
            <Typography
              fontSize={12}
              fontWeight={700}
              color={"var(--neutral-900)"}
            >
              Partners & Revenue Sharing:
            </Typography>
          </Grid2>
          <Grid2 container size={12} spacing={3}>
            <Grid2
              container
              size={4}
              p={2}
              border={"1px solid var(--neutral-100)"}
              borderRadius={2}
              bgcolor={"var(--white)"}
              spacing={1}
            >
              <Grid2
                size={12}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--neutral-700)"
                >
                  {data?.data?.partnerSharing?.partnerName || "-"}
                </Typography>
                <Tooltip title={"Edit"} arrow placement="top">
                  <IconButton
                    onClick={() =>
                      setOpenActionModal({
                        open: true,
                        type: "REVENUE_SHARING",
                        modalTitle: "Edit Revenue Sharing",
                        width: "300px",
                        subTitle: data?.data?.partnerSharing?.partnerName,
                      })
                    }
                  >
                    <Edit1Icon />
                  </IconButton>
                </Tooltip>
              </Grid2>
              <Grid2
                size={12}
                p={"16px 0"}
                borderBottom={"1px solid var(--neutral-100)"}
              >
                <Typography
                  fontSize={13}
                  fontWeight={500}
                  color="var(--neutral-700)"
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                >
                  Management fees:
                  <Typography
                    fontSize={13}
                    fontWeight={600}
                    color="var(--grey-900)"
                    component={"span"}
                  >
                    {handlePercentage(
                      data?.data?.partnerSharing
                        ?.managementFeeSharingPercentage,
                    )}
                  </Typography>
                </Typography>
              </Grid2>
              <Grid2 size={12} p={"16px 0"}>
                <Typography
                  fontSize={13}
                  fontWeight={500}
                  color="var(--neutral-700)"
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                >
                  Performance fees:
                  <Typography
                    fontSize={13}
                    fontWeight={600}
                    color="var(--grey-900)"
                    component={"span"}
                  >
                    {handlePercentage(
                      data?.data?.partnerSharing
                        ?.performanceFeeSharingPercentage,
                    )}
                  </Typography>
                </Typography>
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
      </Show.When>

      <Show.When isTrue={!data?.data?.partnerSharing?.partner}>
        <Grid2 size={12}>
          <Typography
            fontSize={12}
            fontWeight={700}
            color={"var(--neutral-900)"}
          >
            Partners & Revenue Sharing:
          </Typography>
        </Grid2>
        <Grid2
          container
          p={3}
          border={"1px solid var(--neutral-100)"}
          borderRadius={2}
          bgcolor={"var(--white)"}
          spacing={1}
          size={4}
        >
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Typography
              fontSize={14}
              fontWeight={700}
              color={"var(--neutral-900)"}
            >
              No Partner Added Yet
            </Typography>
          </Grid2>
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Typography
              fontSize={12}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              Use the link below to navigate and add a partner.
            </Typography>
          </Grid2>
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <GKButton
              variant="text"
              startIcon={<Plus1Icon />}
              onClick={handleRedirectProfileTab}
            >
              Add Partner
            </GKButton>
          </Grid2>
        </Grid2>
      </Show.When>

      <Show.When isTrue={!!anchorEl}>
        <BillingDateModal
          open={anchorEl}
          setOpen={setAnchorEl}
          portfolioId={props?.portfolioId}
          billingDate={data?.data?.feePlan?.startingDate}
        />
      </Show.When>

      <Show.When isTrue={openActionModal.open}>
        <RevenueActionModal
          open={openActionModal}
          setOpen={setOpenActionModal}
          feePlanDetails={data?.data}
          portfolioId={props?.portfolioId}
        />
      </Show.When>
    </Grid2>
  );
};

export default RevenueDetailsTab;
